import { Box, Container, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type FaqItemT = { question: string; answer: string };

type FaqAccordionPropsT = { items: FaqItemT[] };

const FaqAccordion = ({ items }: FaqAccordionPropsT) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <Box>
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const paragraphs = item.answer.split("\n\n");
        return (
          <Box key={item.question} sx={isOpen ? styles.faqItemOpen : styles.faqItemClosed}>
            <Box onClick={() => setOpenIndex(isOpen ? null : index)} sx={styles.faqItemHeader}>
              <Typography sx={isOpen ? styles.faqQuestionOpen : styles.faqQuestionClosed}>{item.question}</Typography>
              <Typography sx={styles.faqToggleIcon}>{isOpen ? "\u2212" : "+"}</Typography>
            </Box>
            {isOpen && (
              <Box sx={styles.faqAnswerContainer}>
                {paragraphs.map((para, pIndex) => (
                  <Typography
                    key={para.slice(0, 40)}
                    sx={{
                      ...styles.faqAnswerParagraph,
                      mb: pIndex < paragraphs.length - 1 ? 2 : 0,
                    }}
                  >
                    {para}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

type VideoCardDataT = {
  category: string;
  title: string;
  vimeoSrc: string;
  thumb: string;
};

const useInView = (threshold = 0.15) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { containerRef, inView };
};

type VideoCardPropsT = {
  data: VideoCardDataT;
  animDelay: number;
  visible: boolean;
};

const VideoCard = ({ data, animDelay, visible }: VideoCardPropsT) => {
  const [hovered, setHovered] = useState(false);
  const [playing, setPlaying] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(16px)",
        border: hovered ? "1px solid rgba(56,189,248,0.4)" : "1px solid rgba(56,189,248,0.12)",
        borderRadius: "16px",
        overflow: "hidden",
        transition: "transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease, opacity 0.5s ease",
        transform: (() => {
          if (!visible) return "translateY(20px)";
          return hovered ? "translateY(-6px)" : "translateY(0)";
        })(),
        boxShadow: hovered ? "0 0 30px rgba(56,189,248,0.1)" : "none",
        opacity: visible ? 1 : 0,
        transitionDelay: visible ? `${animDelay}ms` : "0ms",
      }}
    >
      <Box sx={{ position: "relative", paddingTop: "122%" }}>
        {playing ? (
          <Box
            component="iframe"
            src={`${data.vimeoSrc}&autoplay=1`}
            frameBorder={0}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            referrerPolicy={"strict-origin-when-cross-origin" as React.HTMLAttributeReferrerPolicy}
            allowFullScreen
            sx={styles.videoCardIframe}
          />
        ) : (
          <Box
            onClick={() => setPlaying(true)}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${data.thumb})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={hovered ? styles.videoCardOverlayHovered : styles.videoCardOverlay} />
            <Box
              sx={{
                position: "relative",
                width: 48,
                height: 48,
                background: hovered ? "rgba(56,189,248,0.3)" : "rgba(56,189,248,0.15)",
                backdropFilter: "blur(8px)",
                border: "2px solid rgba(56,189,248,0.7)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.25s ease",
                transform: hovered ? "scale(1.1)" : "scale(1)",
                boxShadow: hovered ? "0 0 20px rgba(56,189,248,0.4)" : "none",
              }}
            >
              <Box sx={styles.playButtonTriangle} />
            </Box>
            <Box sx={styles.videoCardGradientOverlay}>
              <Typography sx={styles.videoCardCategory}>{data.category}</Typography>
              <Typography sx={styles.videoCardTitle}>{data.title}</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const INTRO_VIMEO_BG =
  "https://player.vimeo.com/video/1181305301?h=6ddb5dcf71&background=1&autoplay=1&loop=1&muted=1&badge=0&autopause=0&player_id=0&app_id=58479";
const INTRO_VIMEO_PLAY =
  "https://player.vimeo.com/video/1181305301?h=6ddb5dcf71&autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479";

type FAQContentPropsT = { t: TranslateFnT };

const FAQContent = ({ t }: FAQContentPropsT) => {
  const [playing, setPlaying] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);
  const { containerRef: gridRef, inView: gridInView } = useInView(0.1);

  const tutorials: VideoCardDataT[] = [
    {
      category: t("tutorials.videos.video1.category"),
      title: t("tutorials.videos.video1.title"),
      vimeoSrc: "https://player.vimeo.com/video/701039467?h=0a285343df&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-1.png",
    },
    {
      category: t("tutorials.videos.video2.category"),
      title: t("tutorials.videos.video2.title"),
      vimeoSrc:
        "https://player.vimeo.com/video/701034316?h=a08ba47bb0&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-2.png",
    },
    {
      category: t("tutorials.videos.video3.category"),
      title: t("tutorials.videos.video3.title"),
      vimeoSrc:
        "https://player.vimeo.com/video/701035496?h=96a90a5806&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-3.png",
    },
    {
      category: t("tutorials.videos.video4.category"),
      title: t("tutorials.videos.video4.title"),
      vimeoSrc:
        "https://player.vimeo.com/video/701040792?h=221824d1b9&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-4.png",
    },
    {
      category: t("tutorials.videos.video5.category"),
      title: t("tutorials.videos.video5.title"),
      vimeoSrc:
        "https://player.vimeo.com/video/701041429?h=f4cd762da8&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-5.png",
    },
    {
      category: t("tutorials.videos.video6.category"),
      title: t("tutorials.videos.video6.title"),
      vimeoSrc:
        "https://player.vimeo.com/video/701037080?h=63506b1cb5&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-6.png",
    },
    {
      category: t("tutorials.videos.video7.category"),
      title: t("tutorials.videos.video7.title"),
      vimeoSrc:
        "https://player.vimeo.com/video/701042492?h=86fdac0a66&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-7.png",
    },
    {
      category: t("tutorials.videos.video8.category"),
      title: t("tutorials.videos.video8.title"),
      vimeoSrc:
        "https://player.vimeo.com/video/701043667?h=0b5cba957c&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479",
      thumb: "/img/assets/faq-8.png",
    },
  ];

  type FaqRawT = { q: string; a: string };

  const faqQuestionKeys = [
    "question1",
    "question2",
    "question3",
    "question4",
    "question5",
    "question6",
    "question7",
    "question8",
    "question9",
    "question10",
  ];

  const FAQ_ITEMS: FaqItemT[] = faqQuestionKeys.map((key) => {
    const raw = t(`faq.questions.${key}`, { returnObjects: true }) as unknown as FaqRawT;
    return { question: raw.q, answer: raw.a };
  });

  return (
    <Box>
      <Box sx={{ ...styles.heroSection, zIndex: playing ? 1201 : undefined }}>
        <Box sx={styles.heroVideoContainer}>
          <Box
            component="iframe"
            src={playing ? INTRO_VIMEO_PLAY : INTRO_VIMEO_BG}
            frameBorder={0}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            referrerPolicy={"strict-origin-when-cross-origin" as React.HTMLAttributeReferrerPolicy}
            allowFullScreen
            sx={styles.heroVideoIframe}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: playing
              ? "rgba(4,9,34,0.15)"
              : "linear-gradient(to top, rgba(4,9,34,0.88) 0%, rgba(4,9,34,0.35) 50%, rgba(4,9,34,0.55) 100%)",
            transition: "background 0.6s ease",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            px: { xs: 3, md: 6 },
            opacity: playing ? 0 : 1,
            transform: playing ? "translateY(-20px)" : "translateY(0)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
            pointerEvents: playing ? "none" : "auto",
          }}
        >
          <Typography sx={styles.heroEyebrow}>{t("hero.eyebrow")}</Typography>
          <Typography component="h1" sx={styles.heroHeading}>
            <Box component="span" sx={{ color: "white" }}>
              {t("hero.headlineWhite")}
            </Box>{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("hero.headlineGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.heroSubtext}>{t("hero.subtext")}</Typography>

          <Box
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
            onClick={() => setPlaying(true)}
            sx={{
              width: { xs: 88, md: 108 },
              height: { xs: 88, md: 108 },
              borderRadius: "50%",
              background: btnHovered ? "rgba(56,189,248,0.25)" : "rgba(56,189,248,0.12)",
              backdropFilter: "blur(12px)",
              border: btnHovered ? "2px solid rgba(56,189,248,0.9)" : "2px solid rgba(56,189,248,0.5)",
              boxShadow: btnHovered
                ? "0 0 50px rgba(56,189,248,0.5), 0 0 100px rgba(56,189,248,0.2)"
                : "0 0 30px rgba(56,189,248,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              transform: btnHovered ? "scale(1.1)" : "scale(1)",
            }}
          >
            <Box sx={styles.heroPlayTriangle} />
          </Box>

          <Typography sx={styles.heroStartHere}>{t("hero.startHere")}</Typography>
        </Box>

        {playing && (
          <Box onClick={() => setPlaying(false)} sx={styles.closeButton}>
            <CloseIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
        )}

        <Box sx={styles.bottomGradient} />
      </Box>

      <Box sx={{ background: "#040922", py: { xs: 10, md: 14 }, position: "relative", overflow: "hidden" }}>
        <Box sx={styles.tutorialsSectionBg} />
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={styles.sectionEyebrow}>{t("tutorials.eyebrow")}</Typography>
          <Typography component="h2" sx={styles.tutorialsSectionHeading}>
            {t("tutorials.heading")}{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("tutorials.headingGradient")}
            </Box>{" "}
            {t("tutorials.headingSuffix")}
          </Typography>
          <Typography sx={styles.sectionSubtext}>{t("tutorials.subtext")}</Typography>
          <Box ref={gridRef} sx={styles.videoGrid}>
            {tutorials.map((video, i) => (
              <VideoCard key={video.title} data={video} animDelay={i * 60} visible={gridInView} />
            ))}
          </Box>
        </Container>
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, mt: { xs: 14, md: 20 } }}>
          <Typography sx={styles.sectionEyebrow}>{t("faq.eyebrow")}</Typography>
          <Typography component="h2" sx={styles.faqSectionHeading}>
            <Box component="span" sx={{ color: "white" }}>
              {t("faq.headingWhite")}
            </Box>{" "}
            <Box component="span" sx={styles.gradientText}>
              {t("faq.headingGradient")}
            </Box>
          </Typography>
          <Typography sx={styles.faqSectionSubtext}>{t("faq.subtext")}</Typography>
          <FaqAccordion items={FAQ_ITEMS} />
        </Container>
      </Box>
    </Box>
  );
};

const FAQPage = () => {
  const { t } = useTranslation("static_faq");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      canonicalUrl={t("meta.canonicalUrl")}
      noTopSpacer
    >
      <FAQContent t={t} />
    </StaticPageLayout>
  );
};
export default FAQPage;
